import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { t } from 'i18next';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faDownload,
  faArrowUpRightFromSquare,
  faCheck,
  faBookOpen,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import './addDocsModal.css';

/**
 * Single-policy reader + acknowledgement view.
 * Shares the exact layout/styling of the Add/Edit document modal
 * (`adm-*` classes): centered popup, document on the left, panels on
 * the right — so "view" and "add" look like the same screen.
 */
interface PolicyViewProps {
  doc: any;
  baseUrl: any;
  access: any;
  category: any[];
  refreshCategoryDocs: () => void;
  handleClosePolicy: () => void;
}

const PolicyView: React.FC<PolicyViewProps> = ({
  doc,
  baseUrl,
  access,
  category,
  refreshCategoryDocs,
  handleClosePolicy,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const isLearned = doc?.needToLearnByUser?.status === 'learned';
  const isAssigned = !!doc?.needToLearnByUser;

  const [reviewed, setReviewed] = useState(false);      // PDF loaded / user has seen it
  const [confirmed, setConfirmed] = useState(false);    // checkbox ticked
  const [acknowledged, setAcknowledged] = useState(isLearned);
  const [submitting, setSubmitting] = useState(false);

  const fileHref = doc?.file_url ? `${baseUrl.replace(/\/$/, '')}${doc.file_url.startsWith('/') ? '' : '/'}${doc.file_url}` : '';
  const hasContent = !!doc?.content;
  const isDoc = hasContent && !fileHref;

  // documents written in the built-in editor have no file to "load",
  // so mark them reviewed once shown (mirrors the iframe onLoad path)
  useEffect(() => {
    if (!fileHref && hasContent) setReviewed(true);
  }, [fileHref, hasContent]);

  // category breadcrumb — mirrors the path builder in Docs.tsx
  const categoryPath = useMemo(() => {
    const current = category?.find((c: any) => c.id === doc?.documentCategoryId);
    if (!current) return '';
    const parent = category?.find((c: any) => c.id === current.parentId);
    return parent ? `${parent.name} · ${current.name}` : current.name;
  }, [category, doc]);

  // acknowledged-by breakdown (admins receive document_learn on the doc)
  const learnedList = (doc?.document_learn || []).filter((e: any) => e.status === 'learned');
  const newList = (doc?.document_learn || []).filter((e: any) => e.status === 'new');

  // progress reflects the 3 steps: opened → reviewed → acknowledged
  const progress = acknowledged ? 100 : reviewed ? 66 : 33;

  const handleAcknowledge = async () => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    try {
      await axios.put(`document-learn/status/${doc.id}`, { status: 'learned' });
      setAcknowledged(true);
      enqueueSnackbar(t('Success'), { variant: 'success' });
      refreshCategoryDocs();
    } catch (e) {
      enqueueSnackbar(t('Error'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // step model for the tracker
  const steps = [
    { key: 'opened', label: t('Opened'), sub: t('just now'), done: true, active: false },
    {
      key: 'reviewed',
      label: t('Reviewed'),
      sub: reviewed ? t('document loaded') : t('open the document'),
      done: reviewed,
      active: !reviewed,
    },
    {
      key: 'ack',
      label: t('Acknowledge'),
      sub: t('confirm & submit'),
      done: acknowledged,
      active: reviewed && !acknowledged,
    },
  ];

  return (
    <Box className="adm-modal" role="presentation">
      {/* top progress accent */}
      <div className="adm-accent"><span style={{ width: `${progress}%` }} /></div>

      {/* header */}
      <div className="adm-head">
        <button className="adm-hbtn" title={t('Close') as string} onClick={handleClosePolicy}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <div className="adm-ttl">
          <h1>{doc?.title}</h1>
          <div className="adm-sub">
            {categoryPath ? `${categoryPath} · ` : ''}
            {isAssigned
              ? isLearned || acknowledged
                ? t('Accepted')
                : t('requires your acknowledgement')
              : t('not required for you')}
          </div>
        </div>
        {fileHref && (
          <>
            <a className="adm-hbtn" href={fileHref} download title={t('Download') as string}>
              <FontAwesomeIcon icon={faDownload} style={{ fontSize: 13 }} />
            </a>
            <a className="adm-hbtn" href={fileHref} target="_blank" rel="noopener noreferrer" title={t('Open in new tab') as string}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 12 }} />
            </a>
          </>
        )}
      </div>

      {/* body: document (left) + panels (right) */}
      <div className="adm-body">
        {/* LEFT — document */}
        <div className="adm-doc">
          <div className="adm-doc-bar">
            <div className="adm-badge" style={isDoc ? { background: 'linear-gradient(135deg,#7c3aed,#a78bfa)', fontSize: 10 } : { fontSize: 10 }}>
              {isDoc ? 'DOC' : 'PDF'}
            </div>
            <div>
              <div className="adm-t">{doc?.title}</div>
              <div className="adm-s">{doc?.day_learn != null ? `${t('DayLearn')}: ${doc.day_learn}` : t('Document')}</div>
            </div>
          </div>

          <div className="adm-stage">
            {fileHref ? (
              <div className="adm-preview">
                <iframe title={doc?.title || 'document'} src={fileHref} onLoad={() => setReviewed(true)} />
              </div>
            ) : hasContent ? (
              <div className="adm-editor-scroll">
                <div className="adm-sheet policy-content" dangerouslySetInnerHTML={{ __html: doc.content }} />
              </div>
            ) : (
              <div className="adm-preview" style={{ background: 'transparent', opacity: 0.5, fontSize: 14 }}>
                {t('No file attached to this document')}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — panels */}
        <div className="adm-aside">
          <div className="adm-aside-scroll">
            {/* acknowledgement */}
            {isAssigned && (
              <div className="adm-panel">
                <h4>{t('Acknowledgement')}</h4>

                {/* status banner */}
                <div
                  className="flex items-center gap-[10px] px-[14px] py-[12px] rounded-[12px] mb-[14px]"
                  style={{
                    background: acknowledged ? 'rgba(67,201,138,.14)' : reviewed ? 'rgba(37,99,235,.10)' : 'rgba(244,169,66,.12)',
                    border: `1px solid ${acknowledged ? 'rgba(67,201,138,.3)' : reviewed ? 'rgba(37,99,235,.25)' : 'rgba(244,169,66,.25)'}`,
                  }}
                >
                  <FontAwesomeIcon
                    icon={acknowledged ? faCircleCheck : faBookOpen}
                    className="text-[18px]"
                    style={{ color: acknowledged ? '#43c98a' : reviewed ? '#2563eb' : '#f4a942' }}
                  />
                  <span
                    className="text-[12.5px] font-bold leading-snug"
                    style={{ color: acknowledged ? '#43c98a' : reviewed ? '#2563eb' : '#f4a942' }}
                  >
                    {acknowledged
                      ? t('Acknowledged — thank you!')
                      : reviewed
                        ? t('Confirm below to acknowledge')
                        : t('Open the document to continue')}
                  </span>
                </div>

                {/* step tracker */}
                <div className="flex flex-col mb-[14px]">
                  {steps.map((s, i) => (
                    <div key={s.key} className="flex gap-[12px] relative pb-[16px] last:pb-0">
                      {i < steps.length - 1 && (
                        <span className="absolute left-[13px] top-[28px] bottom-0 w-[2px]" style={{ background: 'var(--bg-disable)' }} />
                      )}
                      <div
                        className="w-[28px] h-[28px] rounded-full shrink-0 flex items-center justify-center text-[12px] font-extrabold z-[1] transition-all"
                        style={
                          s.done
                            ? { background: '#43c98a', color: '#fff' }
                            : s.active
                              ? { background: '#2563eb', color: '#fff', boxShadow: '0 0 0 4px rgba(37,99,235,.15)' }
                              : { background: 'var(--bg-disable)', color: 'var(--text-color)', opacity: 0.5 }
                        }
                      >
                        {s.done ? <FontAwesomeIcon icon={faCheck} /> : i + 1}
                      </div>
                      <div className="pt-[3px]">
                        <div className={`text-[13px] font-bold ${s.done || s.active ? '' : 'opacity-50'}`}>{s.label}</div>
                        <div className="text-[11px] opacity-45">{s.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* confirm checkbox */}
                <label
                  className={`flex items-start gap-[10px] p-[12px] rounded-[12px] border border-[var(--border-input)] bg-[var(--bg-input)] mb-[14px] ${
                    reviewed && !acknowledged ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px] mt-[1px] shrink-0 accent-[#2563eb] cursor-pointer"
                    disabled={!reviewed || acknowledged}
                    checked={confirmed || acknowledged}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span className="text-[12.5px] leading-snug opacity-80">
                    {t('I confirm I have read and understood')} “{doc?.title}”.
                  </span>
                </label>

                {/* acknowledge button */}
                {acknowledged ? (
                  <div className="flex items-center justify-center gap-[8px] w-full py-[11px] rounded-[12px] text-[13px] font-extrabold"
                    style={{ background: 'rgba(67,201,138,.14)', color: '#43c98a' }}>
                    <FontAwesomeIcon icon={faCheck} /> {t('Accepted')}
                  </div>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!confirmed || submitting}
                    onClick={handleAcknowledge}
                    sx={{ padding: '9px 16px', fontSize: 13, fontWeight: 800, color: 'white !important' }}
                  >
                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: 8 }} /> {t('Acknowledge')}
                  </Button>
                )}
              </div>
            )}

            {/* details */}
            <div className="adm-panel">
              <h4>{t('Details')}</h4>
              <Detail k={t('Category')} v={categoryPath || '—'} />
              {doc?.description && <Detail k={t('Description')} v={doc.description} />}
              {doc?.day_learn != null && <Detail k={t('DayLearn')} v={String(doc.day_learn)} />}
              {access?.description === -1 && (
                <Detail
                  k={t('Informed')}
                  v={`${learnedList.length} ${t('learned')} · ${newList.length} ${t('new')}`}
                />
              )}
            </div>

            {/* acknowledged-by (admins only — relies on document_learn) */}
            {access?.description === -1 && (doc?.document_learn?.length ?? 0) > 0 && (
              <div className="adm-panel">
                <h4>{t('Acknowledged by')}</h4>
                <div className="flex flex-col">
                  {[...learnedList, ...newList]
                    .slice(0, 8)
                    .map((e: any) => (
                      <div key={e.id} className="flex items-center gap-[10px] py-[6px]">
                        <div
                          className="w-[28px] h-[28px] rounded-full shrink-0 flex items-center justify-center text-white text-[10px] font-extrabold"
                          style={{
                            background:
                              e.status === 'learned'
                                ? 'linear-gradient(135deg,#16a34a,#4ade80)'
                                : 'linear-gradient(135deg,#f4a942,#fbbf24)',
                          }}
                        >
                          {(e.user?.first_name?.[0] || '') + (e.user?.last_name?.[0] || '')}
                        </div>
                        <span className="flex-1 text-[12.5px] font-bold truncate">
                          {e.user?.first_name} {e.user?.last_name}
                        </span>
                        <span
                          className="text-[11px] font-extrabold"
                          style={{ color: e.status === 'learned' ? '#43c98a' : '#f4a942' }}
                        >
                          {e.status === 'learned' ? '✓' : '⏳'}
                        </span>
                      </div>
                    ))}
                  {doc.document_learn.length > 8 && (
                    <div className="text-[11px] opacity-50 pt-[6px]">
                      +{doc.document_learn.length - 8} {t('more')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Box>
  );
};

const Detail: React.FC<{ k: any; v: any }> = ({ k, v }) => (
  <div className="flex justify-between items-start gap-[10px] py-[8px] border-b border-[var(--border-input-two)] last:border-b-0">
    <span className="text-[12.5px] opacity-50 shrink-0">{k}</span>
    <span className="text-[12.5px] font-bold text-right break-words">{v}</span>
  </div>
);

export default PolicyView;
